
'use client';
import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import SeoHeading from '../../seo/SeoHeading';
import { HomeSeoSlots, getHomeSlot } from '../../../../src/lib/seo/home-headings';

type Props = {
  headings: HomeSeoSlots;
};

const SpecialOffers: React.FC<Props> = ({ headings }) => {
  const mainSlide = {
    title: 'ایرانـیــــجا',
    description: 'بهترین مکان برای رزرو آنلاین هتل و اقامتگاه با قیمت‌های مناسب و خدمات سریع و مطمئن.',
    buttonText: 'مطالعه بیشتر',
    image: 'images/blog/blog.jpg',
  };

  const bannerSlides = [
    {
      id: 1,
      slotKey: 'specialOffersBanner1Title',
      subtitle: 'پرداخت قسطی با دیجی پی',
      btnText: 'دریافت اعتبار',
      image: 'images/blog/post-test-2.jpg',
    },
    {
      id: 2,
      slotKey: 'specialOffersBanner2Title',
      subtitle: 'تورهای لحظه آخری مشهد',
      btnText: 'مشاهده تورها',
      image: 'images/blog/post-test.jpg',
    },
    {
      id: 3,
      slotKey: 'specialOffersBanner3Title',
      subtitle: 'اقامت در کلبه‌های چوبی',
      btnText: 'رزرو کلبه',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=1000&auto=format&fit=crop',
    },
  ];

  const sectionTitle = getHomeSlot(headings, 'specialOffersTitle');
  const mainTitle = getHomeSlot(headings, 'specialOffersMainTitle');

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextBanner = () => {
    setCurrentIndex((prev) => (prev + 1) % bannerSlides.length);
  };

  const prevBanner = () => {
    setCurrentIndex((prev) => (prev === 0 ? bannerSlides.length - 1 : prev - 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const visibleBanners = isMobile
    ? [bannerSlides[currentIndex]]
    : [bannerSlides[currentIndex], bannerSlides[(currentIndex + 1) % bannerSlides.length]];

  return (
    <div className="offers-wrapper">
      <div className="header-row">
        <SeoHeading slot={sectionTitle} className="special-offers-section-title" />
      </div>

      <div className="main-card">
        <img src={mainSlide.image} alt={mainSlide.title} className="bg-img" />
        <div className="gradient-overlay"></div>
        <div className="content-layer">
          <SeoHeading slot={mainTitle} className="special-offers-main-title" />
          <p className="card-desc">{mainSlide.description}</p>
          <button className="action-btn">{mainSlide.buttonText}</button>
        </div>
      </div>

      <div className="bottom-banners-grid">
        {visibleBanners.map((banner) => {
          const bannerHeading = getHomeSlot(headings, banner.slotKey);

          return (
            <div className="sub-card fade-in" key={`${banner.id}-${currentIndex}`}>
              <img src={banner.image} alt={bannerHeading.text} className="bg-img" />
              <div className="gradient-overlay"></div>
              <div className="content-layer">
                <SeoHeading slot={bannerHeading} className="special-offers-sub-title" />
                <p className="card-subtitle">{banner.subtitle}</p>
                <button className="action-btn">{banner.btnText}</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bottom-controls">
        <button className="nav-btn desktop-only" onClick={prevBanner}>
          <ArrowRight size={22} strokeWidth={2.5} />
        </button>

        <div className="dots-container mobile-only">
          {bannerSlides.map((_, idx) => (
            <span
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`dot ${currentIndex === idx ? 'active-dot' : ''}`}
            ></span>
          ))}
        </div>

        <button className="nav-btn desktop-only" onClick={nextBanner}>
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>
      </div>

      <style jsx>{`
        .offers-wrapper {
          width: 100%;
          max-width: 1250px;
          margin: 40px auto;
          direction: rtl;
          padding: 0 15px;
          box-sizing: border-box;
        }

        .header-row {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 25px;
        }

        :global(.special-offers-section-title) {
          font-size: 24px;
          font-weight: 800;
          color: #4b5563;
          margin: 0;
          text-align: center;
          display: block;
        }

        .bottom-controls {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 40px;
          margin-top: 25px;
        }

        .nav-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #65a30d;
          color: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: 0.2s;
        }

        .nav-btn:hover {
          transform: scale(1.1);
        }

        .main-card {
          width: 100%;
          height: 380px;
          border-radius: 30px;
          overflow: hidden;
          position: relative;
          margin-bottom: 20px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        }

        .bottom-banners-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          width: 100%;
        }

        .sub-card {
          height: 250px;
          border-radius: 25px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }

        .bg-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          z-index: 0;
        }

        .gradient-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.85) 15%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.1) 100%);
          z-index: 1;
        }

        .content-layer {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          align-items: flex-start;
          text-align: right;
          padding: 30px 40px;
          color: white;
        }

        :global(.special-offers-main-title) {
          font-size: 32px;
          font-weight: 900;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 8px rgba(0,0,0,0.6);
          display: block;
        }

        :global(.special-offers-sub-title) {
          font-size: 24px;
          font-weight: 800;
          margin: 0 0 5px 0;
          text-shadow: 0 2px 5px rgba(0,0,0,0.6);
          display: block;
        }

        .card-desc {
          font-size: 16px;
          line-height: 1.7;
          margin-bottom: 20px;
          opacity: 0.95;
          max-width: 550px;
          text-align: right;
        }

        .card-subtitle {
          font-size: 14px;
          margin-bottom: 15px;
          opacity: 0.9;
        }

        .action-btn {
          background: linear-gradient(180deg, #65a30d 0%, #4d7c0f 100%);
          color: white;
          border: none;
          padding: 12px 32px;
          min-width: 150px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          font-family: inherit;
        }

        @keyframes fadeIn {
          from {
            opacity: 0.6;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .fade-in {
          animation: fadeIn 0.4s ease-out;
        }

        .dots-container {
          display: none;
        }

        .mobile-only {
          display: none;
        }

        @media (max-width: 768px) {
          :global(.special-offers-section-title) {
            font-size: 22px;
            color: #374151;
          }

          .desktop-only {
            display: none !important;
          }

          .mobile-only {
            display: flex !important;
          }

          .main-card {
            height: 280px;
            border-radius: 20px;
            margin-bottom: 16px;
          }

          .bottom-banners-grid {
            grid-template-columns: 1fr;
          }

          .sub-card {
            height: 240px;
            border-radius: 20px;
          }

          .content-layer {
            align-items: center;
            text-align: center;
            padding: 20px 20px 30px 20px;
          }

          :global(.special-offers-main-title) {
            font-size: 22px;
            margin-bottom: 8px;
            text-align: center;
          }

          :global(.special-offers-sub-title) {
            font-size: 20px;
            text-align: center;
          }

          .card-desc {
            text-align: center;
            font-size: 13px;
            margin-bottom: 16px;
            line-height: 1.6;
            max-width: 90%;
            margin-left: auto;
            margin-right: auto;
          }

          .card-subtitle {
            font-size: 14px;
            margin-bottom: 16px;
            text-align: center;
          }

          .action-btn {
            width: 85%;
            max-width: 300px;
            padding: 10px 0;
            font-size: 14px;
            border-radius: 10px;
          }

          .bottom-controls {
            margin-top: 15px;
            gap: 0;
          }

          .dots-container {
            display: flex;
            justify-content: center;
            gap: 6px;
          }

          .dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #9ca3af;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .active-dot {
            width: 28px;
            height: 8px;
            border-radius: 4px;
            background-color: #1e40af;
          }
        }
      `}</style>
    </div>
  );
};

export default SpecialOffers;